import { useQuery } from '@tanstack/react-query';
import { useMMKVString } from 'react-native-mmkv';

import apiClient from '@/constants/http';
import { storage, storageKeys } from '@/constants/storage';

import type {
  AppointmentLookupError,
  AppointmentLookupRequest,
  AppointmentLookupResponse,
  UseAppointmentLookupQueryProps,
} from './types';

export const APPOINTMENT_LOOKUP_QUERY_KEY = ['appointment-lookup'] as const;

const fetchAppointmentLookup = async (
  request: AppointmentLookupRequest,
): Promise<AppointmentLookupResponse> => {
  try {
    const { data } = await apiClient.get<AppointmentLookupResponse>(
      `/v1/reservation/${request.confirmationCode}`,
    );

    return data;
  } catch (error: any) {
    const apiError: AppointmentLookupError = {
      message:
        error?.response?.data?.message ||
        error?.message ||
        'Failed to lookup appointment',
      code:
        error?.response?.data?.code ||
        error?.code ||
        'APPOINTMENT_LOOKUP_ERROR',
    };
    throw apiError;
  }
};

export const useAppointmentLookupQuery = ({
  confirmationCode,
  enabled = true,
  ...options
}: UseAppointmentLookupQueryProps) => {
  const [storedConfirmationCode = ''] = useMMKVString(
    storageKeys.appointmentConfirmationCode,
    storage,
  );

  const _confirmationCode = confirmationCode || storedConfirmationCode;

  const queryKey = [
    ...APPOINTMENT_LOOKUP_QUERY_KEY,
    _confirmationCode,
  ] as const;

  const query = useQuery<AppointmentLookupResponse, AppointmentLookupError>({
    ...options,
    queryKey,
    enabled: !!_confirmationCode && enabled,
    async queryFn() {
      if (!_confirmationCode) throw new Error('Confirmation code is required');

      return fetchAppointmentLookup({
        confirmationCode: _confirmationCode,
      });
    },
    retry: (failureCount, error) => {
      if (error.code === 'InvalidArgument') {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return { ...query, confirmationCode: _confirmationCode };
};
